import { AppSettings } from '@/AppSettings';
import { crfReadOnly } from '@/utils/CrfFormUtils';
import { PatientStatusEventType, StatusEventForm } from '@/api/dtos';
import { ConditionConfigurationContext, DefaultConditionView, ValidateOn } from '@ngt/forms-core';
import {
    CollaboratingGroupContext,
    CrfForm,
    DatePicker,
    FormBreadcrumbs,
    GroupedFields,
    GroupedTableArrayField,
    IGroupedTableArrayFieldColumn,
    Input,
    InstitutionContext,
    MasterGroupContext,
    PageLayout,
    PatientContext,
    RouteLoading,
    Select,
    Text,
} from '@ngt/opms';
import { Permission, usePermissionsByIds } from '@ngt/opms-bctapi';
import { FC, useCallback, useContext, useMemo } from 'react';

const conditionConfigurationContext = {
    onConditionNotMet: () => {},
    view: DefaultConditionView,
};

const StatusFields: FC = () => {
    const columns = useMemo<Array<IGroupedTableArrayFieldColumn<StatusEventForm>>>(() => [
        {
            propertyPath: 'statusEvent',
            content: <Input component={Select} />,
            TableHeadCellProps: { sx: { minWidth: 260 } },
            TableCellProps: { sx: { minWidth: 260 } },
        },
        {
            propertyPath: 'dateEffective',
            content: <Input component={DatePicker} />,
            TableHeadCellProps: { sx: { minWidth: 190 } },
            TableCellProps: { sx: { minWidth: 190 } },
        },
        {
            propertyPath: 'comments',
            content: <Input component={Text} multiline minRows={2} variant="outlined" />,
            TableHeadCellProps: { sx: { minWidth: 400 } },
            TableCellProps: { sx: { minWidth: 400 } },
        },
    ], []);

    const enableDisableCondition = useCallback((row: any) => !row?.id || row.statusEvent != PatientStatusEventType.OnStudy, []);

    return (
        <GroupedFields>
            <ConditionConfigurationContext.Provider value={conditionConfigurationContext}>
                <GroupedTableArrayField<StatusEventForm>
                    entityName="Status"
                    propertyPath="statusEvents"
                    columns={columns}
                    AddButtonProps={{ sx: { mb: 2 } }}
                    allowDeletion={(value) => value?.statusEvent !== PatientStatusEventType.OnStudy}
                    enableDisableCondition={enableDisableCondition}
                />
            </ConditionConfigurationContext.Provider>
        </GroupedFields>
    );
};

const StatusForm: FC = () => {
    const { data: masterGroup } = useContext(MasterGroupContext);
    const { data: collaboratingGroup } = useContext(CollaboratingGroupContext);
    const { data: institution } = useContext(InstitutionContext);
    const { data: patient } = useContext(PatientContext);

    const {
        permissions: [canUpdateCrf],
        loading: permissionsLoading,
    } = usePermissionsByIds(
        [Permission.OpmsCrfUpdate],
        masterGroup?.id,
        collaboratingGroup?.id,
        institution?.id,
        patient?.id
    );

    if (permissionsLoading) {
        return <RouteLoading />;
    }

    return (
        <PageLayout breadcrumbs={<FormBreadcrumbs />}>
            <CrfForm
                validateOn={ValidateOn.onChange}
                readOnly={crfReadOnly(canUpdateCrf)}
                debug={AppSettings.debug}
            >
                <StatusFields />
            </CrfForm>
        </PageLayout>
    );
};

export default StatusForm;
